import { supabase } from '@/integrations/supabase/client';
import type { DrillCard } from '@/features/swing-lab/ai/types';

type VideoSection = {
  url?: string;
  title?: string;
  key_points?: string[];
};

type DrillRow = {
  id?: string | null;
  slug?: string | null;
  title?: string | null;
  description?: string | null;
  duration_minutes?: number | null;
  category?: string | null;
  mvp_drill_objective?: string | null;
  mvp_success_criteria?: unknown;
  mvp_how_to_execute?: unknown;
  mvp_why_it_matters?: string | null;
  mvp_course_transfer_cue?: string | null;
  mvp_video_sections_json?: unknown;
  mvp_common_mistakes?: unknown;
  how_to_execute_json?: unknown;
  video_sections_json?: unknown;
  common_mistakes_json?: unknown;
  why_it_matters_detailed?: string | null;
};

export type TrainingDrill = DrillCard & {
  slug?: string;
  title?: string;
  description?: string | null;
  category?: string | null;
  duration_minutes?: number | null;
  common_mistakes?: string[];
  difficulty?: string | null;
};

const normalizeStringArray = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(item => String(item)).filter(Boolean);
  }
  return [String(value)];
};

const normalizeMistakes = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return String(item);
        }
        if (typeof item === 'object' && item) {
          const record = item as Record<string, unknown>;
          const description = record.mistake_description || record.description || record.title;
          return description ? String(description) : JSON.stringify(item);
        }
        return '';
      })
      .filter(Boolean);
  }
  return [String(value)];
};

const firstSentence = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const [first] = trimmed.split('.');
  return first ? `${first.trim()}.` : trimmed;
};

const toTrainingDrill = (data: DrillRow, drillId: string): TrainingDrill => {
  const steps = normalizeStringArray(data.mvp_how_to_execute ?? data.how_to_execute_json);
  const whatToLookFor = normalizeStringArray(data.mvp_success_criteria);
  const commonMistakes = normalizeMistakes(data.mvp_common_mistakes ?? data.common_mistakes_json);
  const focusAreaSource = data.mvp_drill_objective || data.description || '';
  const programConnection = data.mvp_why_it_matters || data.why_it_matters_detailed || data.description || '';
  const swingThought = data.mvp_course_transfer_cue || '';

  let videoUrl: string | null = null;
  const rawSections = (Array.isArray(data.mvp_video_sections_json) ? data.mvp_video_sections_json : null)
    || (Array.isArray(data.video_sections_json) ? data.video_sections_json : null);
  if (rawSections && rawSections.length > 0) {
    const sections = rawSections as VideoSection[];
    if (sections[0]?.url) {
      videoUrl = sections[0].url;
    }
  }

  return {
    drill_id: data.slug || drillId,
    drill_name: data.title || 'Drill',
    slug: data.slug || drillId,
    title: data.title || 'Drill',
    description: data.description ?? null,
    focus_area: firstSentence(focusAreaSource),
    program_connection_text: programConnection,
    swing_thought: swingThought,
    steps,
    assigned_reps: {
      sets: 0,
      reps_per_set: 0,
      rest_seconds: 0
    },
    what_to_look_for: whatToLookFor,
    video_url: videoUrl,
    category: data.category ?? null,
    duration_minutes: data.duration_minutes ?? null,
    common_mistakes: commonMistakes,
    difficulty: (data as Record<string, unknown>).difficulty as string | null ?? null,
    mvp_drill_objective: data.mvp_drill_objective ?? null,
  };
};

/**
 * PLA-138: Fallback drill data when database is empty
 * These slugs MUST match 20260202000000_pla133_canonical_drills.sql
 */
const FALLBACK_DRILLS: Record<string, Partial<DrillRow>> = {
  'keep-hip-depth': {
    slug: 'keep-hip-depth',
    title: 'Keep Hip Depth Through Impact',
    description: 'Train to keep your hips back while arms swing through',
    mvp_drill_objective: 'You are training the ability to keep your hips back while your arms swing through.',
    mvp_why_it_matters: 'Early extension often shows up as thin shots, blocks, or a loss of space for the arms through impact.',
    mvp_how_to_execute: ['Place an alignment stick behind you touching your hips', 'Make a slow swing keeping light contact', 'Swing through to a balanced finish'],
    mvp_course_transfer_cue: 'Keep your hips lightly touching the stick as you swing through.',
    mvp_success_criteria: ['Stick stays in contact through impact', 'Contact feels solid'],
    mvp_common_mistakes: ['Pushing hips into the stick', 'Standing up to avoid the stick'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'brush-ground-impact': {
    slug: 'brush-ground-impact',
    title: 'Brush the Ground Through Impact',
    description: 'Keep your chest over the ball while the club brushes the ground',
    mvp_drill_objective: 'You are training the ability to keep your chest over the ball while the club brushes the ground.',
    mvp_why_it_matters: 'Loss of posture causes inconsistent strikes. This drill keeps your spine angle stable.',
    mvp_how_to_execute: ['Set up with a short iron', 'Place a tee in front of the ball', 'Make a slow swing brushing the ground'],
    mvp_course_transfer_cue: 'Keep your chest down as the club brushes the ground.',
    mvp_success_criteria: ['Brush mark just after the ball', 'Head height stays steady'],
    mvp_common_mistakes: ['Lifting chest to avoid ground', 'Sliding laterally'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'pump-to-inside': {
    slug: 'pump-to-inside',
    title: 'Pump to the Inside',
    description: 'Train the club to drop inside for a better path',
    mvp_drill_objective: 'You are training the club to drop inside during the transition.',
    mvp_why_it_matters: 'Over-the-top moves cause slices and pulls. This drill trains an inside path.',
    mvp_how_to_execute: ['Take a half backswing', 'Pump the club down without hitting', 'Feel the club drop inside'],
    mvp_course_transfer_cue: 'Feel the club drop into the slot before turning through.',
    mvp_success_criteria: ['Club drops inside on each pump', 'Path feels more inside-out'],
    mvp_common_mistakes: ['Casting from the top', 'Not pausing at the top'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'hold-angle-impact': {
    slug: 'hold-angle-impact',
    title: 'Hold the Angle Through Impact',
    description: 'Promote forward shaft lean and delayed release',
    mvp_drill_objective: 'You are training the ability to maintain wrist angle through impact.',
    mvp_why_it_matters: 'Casting loses power and strike control. This drill promotes proper release timing.',
    mvp_how_to_execute: ['Make a half swing', 'Hold the finish with hands ahead of the clubhead', 'Check shaft lean at impact'],
    mvp_course_transfer_cue: 'Feel your hands lead the clubhead through impact.',
    mvp_success_criteria: ['Shaft leans forward at impact', 'Hands ahead of ball'],
    mvp_common_mistakes: ['Flipping the wrists early', 'Releasing before impact'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'trail-hip-turns-back': {
    slug: 'trail-hip-turns-back',
    title: 'Trail Hip Turns Back',
    description: 'Train trail hip rotation to prevent reverse pivot',
    mvp_drill_objective: 'You are training proper trail hip rotation in the backswing.',
    mvp_why_it_matters: 'Reverse pivot causes poor weight shift and inconsistent contact.',
    mvp_how_to_execute: ['Feel your trail hip turn back', 'Keep weight on inside of trail foot', 'Maintain spine angle'],
    mvp_course_transfer_cue: 'Feel your trail hip pocket move behind you.',
    mvp_success_criteria: ['Trail hip turns back', 'Weight stays centered'],
    mvp_common_mistakes: ['Swaying off the ball', 'Weight moving to outside of foot'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'handle-to-lead-thigh': {
    slug: 'handle-to-lead-thigh',
    title: 'Handle to Lead Thigh',
    description: 'Train hands-ahead position for better low point control',
    mvp_drill_objective: 'You are training the hands-ahead impact position.',
    mvp_why_it_matters: 'Low point control is essential for solid contact.',
    mvp_how_to_execute: ['Set up normally', 'Swing to impact and stop', 'Check that handle points at lead thigh'],
    mvp_course_transfer_cue: 'Feel the handle pointing at your lead thigh at impact.',
    mvp_success_criteria: ['Handle ahead of clubhead', 'Ball-first contact'],
    mvp_common_mistakes: ['Scooping at impact', 'Handle behind the ball'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'tray-face-control': {
    slug: 'tray-face-control',
    title: 'Tray Face Control',
    description: 'Stabilize clubface at top of backswing',
    mvp_drill_objective: 'You are training clubface control at the top of the backswing.',
    mvp_why_it_matters: 'A stable face at the top reduces compensations through impact.',
    mvp_how_to_execute: ['Take a backswing', 'Stop at the top', 'Check that the face matches your forearm'],
    mvp_course_transfer_cue: 'Imagine balancing a tray on your trail palm at the top.',
    mvp_success_criteria: ['Face square to forearm', 'No cupping or bowing'],
    mvp_common_mistakes: ['Cupped wrist at top', 'Face pointing at sky'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'rotate-past-band': {
    slug: 'rotate-past-band',
    title: 'Rotate Past the Band',
    description: 'Use resistance to prevent lateral slide and enforce rotation',
    mvp_drill_objective: 'You are training rotational movement instead of lateral slide.',
    mvp_why_it_matters: 'Excessive slide causes inconsistent low point and power loss.',
    mvp_how_to_execute: ['Place a band around your hips', 'Anchor to your trail side', 'Rotate against the resistance'],
    mvp_course_transfer_cue: 'Rotate your belt buckle to the target, not slide it.',
    mvp_success_criteria: ['Rotation without slide', 'Hips turn not shift'],
    mvp_common_mistakes: ['Sliding into the band', 'Not rotating fully'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'athletic-hinge-check': {
    slug: 'athletic-hinge-check',
    title: 'Athletic Hinge Check',
    description: 'Rehearse proper hip hinge and address posture',
    mvp_drill_objective: 'You are training proper address posture with athletic hip hinge.',
    mvp_why_it_matters: 'Setup drives swing consistency. Poor posture causes compensations.',
    mvp_how_to_execute: ['Stand tall with club across hips', 'Hinge from hips keeping spine straight', 'Feel weight in mid-foot'],
    mvp_course_transfer_cue: 'Feel athletic and balanced at address.',
    mvp_success_criteria: ['Spine straight at hinge', 'Weight balanced'],
    mvp_common_mistakes: ['Rounding the back', 'Weight on heels'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  // PLA-128 Scoring Drills (7 drills)
  'own-short-putts': {
    slug: 'own-short-putts',
    title: 'Own the Short Putts',
    description: 'Build confidence from inside six feet',
    mvp_drill_objective: 'You are training the ability to start the ball on line with a calm, repeatable stroke.',
    mvp_why_it_matters: 'Short putts decide score on most rounds. This drill builds make-rate confidence.',
    mvp_how_to_execute: ['Place four balls in a circle, 4-6 feet from hole', 'Putt one by one, resetting aim each time', 'Complete two full circles'],
    mvp_course_transfer_cue: 'Roll it on line with a quiet finish.',
    mvp_success_criteria: ['Ball starts on intended line', 'Finish is steady'],
    mvp_common_mistakes: ['Rushing the next putt', 'Steering the putter head'],
    duration_minutes: 8,
    category: 'putting'
  },
  'speed-window-control': {
    slug: 'speed-window-control',
    title: 'Speed Window Control',
    description: 'Leave lag putts within a tight window',
    mvp_drill_objective: 'You are training the ability to leave the ball within a tight distance window past the hole.',
    mvp_why_it_matters: 'Good lag putting prevents three-putts and saves strokes fast.',
    mvp_how_to_execute: ['Choose a hole 20-30 feet away', 'Place tees 1 foot short and 2 feet past', 'Hit 10 putts to land in the window'],
    mvp_course_transfer_cue: 'Let the ball die in the window.',
    mvp_success_criteria: ['Most putts finish between tees', 'Stroke length matches distance'],
    mvp_common_mistakes: ['Changing aim to chase speed errors', 'Hitting harder instead of lengthening stroke'],
    duration_minutes: 7,
    category: 'putting'
  },
  'straight-start-gate': {
    slug: 'straight-start-gate',
    title: 'Straight-Start Gate',
    description: 'Start the ball on your chosen line',
    mvp_drill_objective: 'You are training the ability to start the ball on your chosen line.',
    mvp_why_it_matters: 'Starting the ball on line makes every putt easier to read and trust.',
    mvp_how_to_execute: ['Place two tees just wider than a ball, 12-18 inches in front', 'Putt 8 balls through the gate', 'Track how many go through cleanly'],
    mvp_course_transfer_cue: 'Roll it through the gate.',
    mvp_success_criteria: ['Ball passes cleanly between tees', 'Contact sounds solid'],
    mvp_common_mistakes: ['Widening the gate', 'Slowing down through impact'],
    duration_minutes: 6,
    category: 'putting'
  },
  'low-runner-landing': {
    slug: 'low-runner-landing',
    title: 'Low Runner Landing',
    description: 'Land on a spot and let it roll out',
    mvp_drill_objective: 'You are training the ability to land the ball on a small spot and let it roll out.',
    mvp_why_it_matters: 'A low, predictable chip gets the ball on the green quickly.',
    mvp_how_to_execute: ['Pick a target spot on the fringe', 'Use a lower-lofted club', 'Make a putting-like stroke to land on the marker'],
    mvp_course_transfer_cue: 'Land it on the spot and let it run.',
    mvp_success_criteria: ['Ball lands close to marker', 'Roll-out is smooth'],
    mvp_common_mistakes: ['Trying to lift the ball with hands', 'Moving ball too far forward'],
    duration_minutes: 8,
    category: 'short_game'
  },
  'consistent-arm-length': {
    slug: 'consistent-arm-length',
    title: 'Consistent Arm Length',
    description: 'Match backswing length with smooth finish',
    mvp_drill_objective: 'You are training the ability to match a fixed backswing length with a smooth finish.',
    mvp_why_it_matters: 'Distance control with wedges comes from repeatable swing length.',
    mvp_how_to_execute: ['Choose a target 15-30 yards away', 'Rehearse backswing with lead arm parallel', 'Hit 8 shots with same length'],
    mvp_course_transfer_cue: 'Same backswing, smooth finish.',
    mvp_success_criteria: ['Backswing length looks the same', 'Shots land in tight cluster'],
    mvp_common_mistakes: ['Speeding up to force distance', 'Letting backswing drift longer'],
    duration_minutes: 7,
    category: 'short_game'
  },
  'clean-sand-entry': {
    slug: 'clean-sand-entry',
    title: 'Clean Sand Entry',
    description: 'Enter the sand at the same spot every time',
    mvp_drill_objective: 'You are training the ability to enter the sand at the same spot every time.',
    mvp_why_it_matters: 'Bunker shots rely on striking the sand before the ball.',
    mvp_how_to_execute: ['Draw a line 2 inches behind ball position', 'Make swings focused on hitting the line', 'Check entry mark after each swing'],
    mvp_course_transfer_cue: 'Hit the line, splash the sand.',
    mvp_success_criteria: ['Club enters sand on the line', 'Sand splashes forward'],
    mvp_common_mistakes: ['Trying to pick ball clean', 'Hitting too far behind'],
    duration_minutes: 8,
    category: 'bunker'
  },
  'forward-base-splash': {
    slug: 'forward-base-splash',
    title: 'Forward-Base Splash',
    description: 'Keep pressure forward while club slides under',
    mvp_drill_objective: 'You are training the ability to keep pressure forward while the club slides under the ball.',
    mvp_why_it_matters: 'Staying forward in the bunker prevents digging.',
    mvp_how_to_execute: ['Set up with 60-70% weight on lead foot', 'Open the face slightly', 'Finish with weight still forward'],
    mvp_course_transfer_cue: 'Stay forward and splash through.',
    mvp_success_criteria: ['Lead foot stays planted', 'Club slides through sand'],
    mvp_common_mistakes: ['Shifting weight to trail foot', 'Finishing on back foot'],
    duration_minutes: 6,
    category: 'bunker'
  },
  // PLA-127 Additional Swing Drills (14)
  'barriers-hip-rotation': {
    slug: 'barriers-hip-rotation',
    title: 'Barriers to Hip Rotation',
    description: 'Use physical barriers to force rotation instead of forward thrust',
    mvp_drill_objective: 'You are training the ability to rotate your hips without moving them toward the ball.',
    mvp_why_it_matters: 'Early extension pushes the hips toward the ball. Barriers force rotation.',
    mvp_how_to_execute: ['Place two golf balls outside each toe', 'Make a slow swing', 'Turn hips without knocking barriers over'],
    mvp_course_transfer_cue: 'Rotate without knocking the barriers over.',
    mvp_success_criteria: ['Barriers remain in place', 'Hips rotate not thrust'],
    mvp_common_mistakes: ['Knocking barriers over', 'Lifting up to avoid barriers'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'braced-lead-leg-load': {
    slug: 'braced-lead-leg-load',
    title: 'Braced Lead Leg Load',
    description: 'Use resistance to teach loading into the lead hip without standing up',
    mvp_drill_objective: 'You are training the ability to load into your lead side without losing your posture.',
    mvp_why_it_matters: 'Early extension often comes from not loading properly.',
    mvp_how_to_execute: ['Anchor band around lead leg', 'Rotate into resistance', 'Finish balanced on lead side'],
    mvp_course_transfer_cue: 'Load into the lead hip without standing up.',
    mvp_success_criteria: ['Feel resistance in lead hip', 'Posture stays stable'],
    mvp_common_mistakes: ['Standing up to escape resistance', 'Sliding laterally'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'step-turn-through': {
    slug: 'step-turn-through',
    title: 'Step and Turn Through',
    description: 'Rebuild proper step-then-turn separation',
    mvp_drill_objective: 'You are training the ability to shift weight before rotating through.',
    mvp_why_it_matters: 'Early extension disrupts the sequence of weight shift and rotation.',
    mvp_how_to_execute: ['Start with feet together', 'Step lead foot toward target', 'Rotate hips as foot lands'],
    mvp_course_transfer_cue: 'Step, then turn through.',
    mvp_success_criteria: ['Step before shoulders unwind', 'Hips clear through'],
    mvp_common_mistakes: ['Spinning shoulders before stepping', 'Stepping too wide'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'sidearm-path-feel': {
    slug: 'sidearm-path-feel',
    title: 'Sidearm Path Feel',
    description: 'Use sidearm throwing motion to train in-to-out path',
    mvp_drill_objective: 'You are training the ability to swing on an in-to-out path.',
    mvp_why_it_matters: 'Over-the-top swings come from the club moving outside the target line.',
    mvp_how_to_execute: ['Practice sidearm throwing motion', 'Rehearse feeling with club', 'Hit ball with sidearm feel'],
    mvp_course_transfer_cue: 'Throw sidearm, not overhand.',
    mvp_success_criteria: ['Club drops inside', 'Slice curve reduced'],
    mvp_common_mistakes: ['Throwing overhand', 'Rushing without establishing feel'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'lift-and-drop': {
    slug: 'lift-and-drop',
    title: 'Lift and Drop',
    description: 'Exaggerate the drop to shallow the club',
    mvp_drill_objective: 'You are training the ability to lift the hands at the top and drop them into the slot.',
    mvp_why_it_matters: 'Over-the-top moves start with shoulders instead of hands dropping.',
    mvp_how_to_execute: ['Pause at top and lift hands up', 'Drop hands toward trail hip', 'Complete swing through'],
    mvp_course_transfer_cue: 'Lift, then drop.',
    mvp_success_criteria: ['Club shallows in downswing', 'Ball starts closer to line'],
    mvp_common_mistakes: ['Spinning shoulders instead of lifting', 'Rushing the drop'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'early-wrist-set': {
    slug: 'early-wrist-set',
    title: 'Early Wrist Set',
    description: 'Set wrists early to maintain lag longer',
    mvp_drill_objective: 'You are training the ability to set your wrists early and maintain that angle.',
    mvp_why_it_matters: 'Casting releases the club early because wrists never set properly.',
    mvp_how_to_execute: ['Hinge wrists fully by hip height', 'Pause and check angle', 'Swing through holding angle'],
    mvp_course_transfer_cue: 'Set early, hold late.',
    mvp_success_criteria: ['Wrists fully hinged by hip height', 'Angle holds until impact'],
    mvp_common_mistakes: ['Setting wrists too late', 'Releasing early'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'rod-check-chips': {
    slug: 'rod-check-chips',
    title: 'Rod Check Chips',
    description: 'Instant feedback on wrist flip using alignment rod',
    mvp_drill_objective: 'You are training the ability to maintain wrist angle through impact.',
    mvp_why_it_matters: 'Casting flips the wrists before impact.',
    mvp_how_to_execute: ['Tape rod to grip extending past butt', 'Chip without rod hitting lead side', 'Reset if rod hits'],
    mvp_course_transfer_cue: 'Hands lead, rod stays clear.',
    mvp_success_criteria: ['Rod never touches lead side', 'Hands ahead of clubhead'],
    mvp_common_mistakes: ['Flipping wrists and hitting yourself', 'Scooping to lift ball'],
    duration_minutes: 8,
    category: 'short_game'
  },
  'arms-high-rotation': {
    slug: 'arms-high-rotation',
    title: 'Arms High Rotation',
    description: 'Keep arms high while rotating to lock in spine angle',
    mvp_drill_objective: 'You are training the ability to rotate around a fixed spine angle.',
    mvp_why_it_matters: 'Loss of posture happens when arms drop and chest lifts.',
    mvp_how_to_execute: ['Lift arms while keeping spine angle', 'Turn shoulders with arms high', 'Lower to address and swing'],
    mvp_course_transfer_cue: 'Arms high, spine stays still.',
    mvp_success_criteria: ['Spine angle constant during lift', 'Rotation feels around fixed axis'],
    mvp_common_mistakes: ['Standing up as arms lift', 'Swaying laterally'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'narrow-stance-balance': {
    slug: 'narrow-stance-balance',
    title: 'Narrow Stance Balance',
    description: 'Amplify posture loss feedback with narrow stance',
    mvp_drill_objective: 'You are training the ability to maintain posture by feeling when you lose it.',
    mvp_why_it_matters: 'Loss of posture is easier to feel with a narrow stance.',
    mvp_how_to_execute: ['Set up with feet almost touching', 'Make short slow swings', 'Widen stance as balance improves'],
    mvp_course_transfer_cue: 'Stay balanced, stay down.',
    mvp_success_criteria: ['Maintain balance throughout', 'Spine angle constant'],
    mvp_common_mistakes: ['Taking too big a swing', 'Standing up to recover'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'trail-foot-pressure-check': {
    slug: 'trail-foot-pressure-check',
    title: 'Trail Foot Pressure Check',
    description: 'Keep weight inside trail foot to prevent sway',
    mvp_drill_objective: 'You are training the ability to load inside your trail foot without swaying.',
    mvp_why_it_matters: 'Sway moves the body laterally off the ball.',
    mvp_how_to_execute: ['Place ball under outside of trail foot', 'Keep pressure inside foot', 'Full swing and finish balanced'],
    mvp_course_transfer_cue: 'Pressure inside, not outside.',
    mvp_success_criteria: ['Ball under foot stays in place', 'Backswing feels rotational'],
    mvp_common_mistakes: ['Crushing ball by swaying', 'Sliding instead of turning'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'trail-hip-stick-check': {
    slug: 'trail-hip-stick-check',
    title: 'Trail Hip Stick Check',
    description: 'Use vertical stick for instant sway feedback',
    mvp_drill_objective: 'You are training the ability to turn your trail hip without sliding it.',
    mvp_why_it_matters: 'Sway pushes the trail hip away from the target.',
    mvp_how_to_execute: ['Place stick vertically outside trail hip', 'Backswing without touching stick', 'Turn through to finish'],
    mvp_course_transfer_cue: 'Turn the hip, not slide it.',
    mvp_success_criteria: ['Stick remains untouched', 'Hip turns behind you'],
    mvp_common_mistakes: ['Sliding into stick', 'Standing up to avoid stick'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'trail-hip-rotation-only': {
    slug: 'trail-hip-rotation-only',
    title: 'Trail Hip Rotation Only',
    description: 'Focus on rotating trail hip around rather than forward',
    mvp_drill_objective: 'You are training the ability to rotate your trail hip around, not toward the target.',
    mvp_why_it_matters: 'Excessive slide pushes hips toward target instead of rotating.',
    mvp_how_to_execute: ['Pause at top of backswing', 'Rotate trail hip around', 'Finish balanced on lead side'],
    mvp_course_transfer_cue: 'Trail hip around, not forward.',
    mvp_success_criteria: ['Trail hip rotates behind', 'Swing feels more rotational'],
    mvp_common_mistakes: ['Pushing hips forward', 'Stalling hips and flipping'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'step-and-clear': {
    slug: 'step-and-clear',
    title: 'Step and Clear',
    description: 'Use step to force hip clearance',
    mvp_drill_objective: 'You are training the ability to step and clear your hips rather than slide.',
    mvp_why_it_matters: 'Slide happens when weight shifts but hips do not clear.',
    mvp_how_to_execute: ['Start feet together', 'Step lead foot and clear hip', 'Finish with hips facing target'],
    mvp_course_transfer_cue: 'Step and clear.',
    mvp_success_criteria: ['Step before shoulders unwind', 'Hips face target'],
    mvp_common_mistakes: ['Sliding without clearing', 'Stepping too wide'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  'pelvic-rotation-punch': {
    slug: 'pelvic-rotation-punch',
    title: 'Pelvic Rotation Punch',
    description: 'Punch pelvis through instead of sliding',
    mvp_drill_objective: 'You are training the ability to rotate your pelvis through the ball.',
    mvp_why_it_matters: 'Arm swingers often slide because they do not use hips properly.',
    mvp_how_to_execute: ['Half backswing', 'Rotate pelvis like punching through', 'Belt buckle faces target at finish'],
    mvp_course_transfer_cue: 'Punch the pelvis through.',
    mvp_success_criteria: ['Pelvis rotates through', 'Belt buckle faces target'],
    mvp_common_mistakes: ['Sliding instead of rotating', 'Arms outrunning body'],
    duration_minutes: 10,
    category: 'full_swing'
  },
  // Additional Scoring Drills (5)
  '5-foot-king': {
    slug: '5-foot-king',
    title: '5 Foot King',
    description: 'Build pressure tolerance from 5 feet',
    mvp_drill_objective: 'You are training the ability to make short putts under pressure.',
    mvp_why_it_matters: 'Short putts decide most rounds.',
    mvp_how_to_execute: ['Place 5 balls 5 feet from hole', 'Make all 5 in a row', 'Start over if you miss'],
    mvp_course_transfer_cue: 'Roll it on line.',
    mvp_success_criteria: ['Ball starts on intended line', 'Stroke is calm and repeatable'],
    mvp_common_mistakes: ['Rushing between putts', 'Getting frustrated'],
    duration_minutes: 10,
    category: 'putting'
  },
  'ladder-speed-control': {
    slug: 'ladder-speed-control',
    title: 'Ladder Speed Control',
    description: 'Teach incremental speed adjustments',
    mvp_drill_objective: 'You are training the ability to adjust speed incrementally for different distances.',
    mvp_why_it_matters: 'Three-putts often come from poor speed on longer putts.',
    mvp_how_to_execute: ['Place tees at 3ft intervals', 'Climb ladder making each putt', 'Go back one if you miss'],
    mvp_course_transfer_cue: 'Match the stroke to the distance.',
    mvp_success_criteria: ['Stroke length changes with distance', 'Speed consistent for each distance'],
    mvp_common_mistakes: ['Using same stroke for all distances', 'Hitting harder instead of lengthening'],
    duration_minutes: 10,
    category: 'putting'
  },
  'bump-run-landing': {
    slug: 'bump-run-landing',
    title: 'Bump and Run Landing',
    description: 'Land on a spot and let the ball run predictably',
    mvp_drill_objective: 'You are training the ability to land on a spot and let it run.',
    mvp_why_it_matters: 'High chips with unpredictable roll cause big misses.',
    mvp_how_to_execute: ['Pick target spot on fringe', 'Use 7 or 8 iron', 'Make putting-like stroke to land on marker'],
    mvp_course_transfer_cue: 'Land it and let it run.',
    mvp_success_criteria: ['Ball lands close to marker', 'Roll-out is smooth'],
    mvp_common_mistakes: ['Trying to lift the ball', 'Using too much backswing'],
    duration_minutes: 8,
    category: 'short_game'
  },
  'line-entry-drill': {
    slug: 'line-entry-drill',
    title: 'Line Entry Drill',
    description: 'Train consistent sand entry',
    mvp_drill_objective: 'You are training the ability to enter the sand at the same spot every time.',
    mvp_why_it_matters: 'Bunker shots fail when club enters too far behind or too close.',
    mvp_how_to_execute: ['Draw line 2 inches behind ball', 'Swing focused on hitting the line', 'Check entry mark after each swing'],
    mvp_course_transfer_cue: 'Hit the line, splash the sand.',
    mvp_success_criteria: ['Club enters on the line', 'Sand splashes forward'],
    mvp_common_mistakes: ['Trying to pick ball clean', 'Hitting too far behind'],
    duration_minutes: 8,
    category: 'bunker'
  },
  'ride-horse-splash': {
    slug: 'ride-horse-splash',
    title: 'Ride the Horse Splash',
    description: 'Stay low and stable through the sand splash',
    mvp_drill_objective: 'You are training the ability to stay low and stable through the splash.',
    mvp_why_it_matters: 'Bunker shots fail when body moves vertically.',
    mvp_how_to_execute: ['Take very wide stance', 'Flex knees deeply like riding a horse', 'Maintain low position through swing'],
    mvp_course_transfer_cue: 'Stay low and splash through.',
    mvp_success_criteria: ['Knees stay flexed', 'Ball pops out softly'],
    mvp_common_mistakes: ['Standing up during swing', 'Closing face at impact'],
    duration_minutes: 8,
    category: 'bunker'
  },
};

// Helper to check if a string is a valid UUID format
const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export async function loadTrainingDrill(drillId: string): Promise<TrainingDrill | null> {
  if (!drillId) return null;

  // Only compare against id column if drillId is a valid UUID, otherwise just use slug
  const isUuidInput = isUUID(drillId);
  
  let query = supabase
    .from('drills')
    .select('id, slug, title, description, duration_minutes, difficulty, category, mvp_drill_objective, mvp_success_criteria, mvp_how_to_execute, mvp_why_it_matters, mvp_course_transfer_cue, mvp_video_sections_json, mvp_common_mistakes, how_to_execute_json, video_sections_json, common_mistakes_json, why_it_matters_detailed, module_id');

  if (isUuidInput) {
    // If it's a UUID, search by id OR slug
    query = query.or(`id.eq.${drillId},slug.eq.${drillId}`);
  } else {
    // If it's a slug (not UUID), only search by slug to avoid PostgreSQL cast error
    query = query.eq('slug', drillId);
  }

  const { data, error } = await query.single();

  if (error && error.code !== 'PGRST116') {
    // PGRST116 = "no rows returned" - not a real error, just means drill not found
    console.error('Failed to load drill from database:', error);
  }

  if (data && !error) {
    return toTrainingDrill(data as DrillRow, drillId);
  }

  // PLA-138: Use fallback drill data if database query fails
  const fallbackData = FALLBACK_DRILLS[drillId];
  if (fallbackData) {
    console.log('[drill-loader] Using fallback drill data for:', drillId);
    return toTrainingDrill(fallbackData as DrillRow, drillId);
  }

  return null;
}

/**
 * Load all available drills from the database and fallbacks
 */
export async function loadAllDrills(): Promise<TrainingDrill[]> {
  const drills: TrainingDrill[] = [];
  const seenSlugs = new Set<string>();

  // First, try to load from database
  const { data, error } = await supabase
    .from('drills')
    .select('id, slug, title, description, duration_minutes, difficulty, category, mvp_drill_objective, mvp_success_criteria, mvp_how_to_execute, mvp_why_it_matters, mvp_course_transfer_cue, mvp_video_sections_json, mvp_common_mistakes, how_to_execute_json, video_sections_json, common_mistakes_json, why_it_matters_detailed, module_id')
    .not('slug', 'is', null)
    .order('title');

  if (error) {
    console.error('Failed to load drills from database:', error);
  }

  if (data && !error) {
    for (const row of data) {
      if (row.slug && !seenSlugs.has(row.slug)) {
        seenSlugs.add(row.slug);
        drills.push(toTrainingDrill(row as DrillRow, row.slug));
      }
    }
  }

  // Add any fallback drills not in database
  for (const [slug, fallbackData] of Object.entries(FALLBACK_DRILLS)) {
    if (!seenSlugs.has(slug)) {
      seenSlugs.add(slug);
      drills.push(toTrainingDrill(fallbackData as DrillRow, slug));
    }
  }

  // Sort by title
  drills.sort((a, b) => (a.title || '').localeCompare(b.title || ''));

  return drills;
}
